import { logger } from 'services';
import { getTabSync } from '../tab-sync';
import { refreshPlanLanesForPhase } from './fetchers';
import { applyPlanEntityMoveTokens, applyPlanEntityReorderTokens } from './plan-lane-collection';
/** Discriminator for the plan-board lane-refresh signal. */ const PLAN_LANE_REFRESH = 'plan-lane-refresh';
/** Discriminator for the optimistic in-lane reorder signal. */ const PLAN_LANE_REORDER = 'plan-lane-reorder';
/** Discriminator for the optimistic cross-phase move signal. */ const PLAN_LANE_MOVE = 'plan-lane-move';
/**
 * Tell other tabs that a phase's lane membership changed.
 *
 * Deliberately NOT a `planLaneCollection` entity broadcast. A lane record's
 * `pageTokens` is a paginated WINDOW over the phase's canonical order, not
 * entity data — tab A may have scrolled three pages deep where tab B has one.
 * Copying A's window into B would silently change what B is showing. Sending a
 * refresh instead keeps the server as the single ordering authority, at the
 * cost of one fetch per peer that actually has that lane loaded.
 *
 * Call after any change to which entities live in a phase: create, delete, or
 * a cross-phase move. A pure field edit does not need it — that rides on the
 * task entity itself.
 */ export const broadcastPlanLaneRefresh = (projectId, phaseId)=>{
    if (projectId == null || phaseId == null) {
        return;
    }
    getTabSync().post({
        t: 'custom',
        k: PLAN_LANE_REFRESH,
        payload: {
            projectId,
            phaseId
        }
    });
};
const isLaneRefreshPayload = (v)=>typeof v === 'object' && v !== null && typeof v.projectId === 'number' && typeof v.phaseId === 'number';
const isLaneReorderPayload = (v)=>isLaneRefreshPayload(v) && Array.isArray(v.newOrder) && v.newOrder.every((t)=>typeof t === 'string');
/**
 * Tell other tabs a phase's in-lane ORDER changed, before the server has
 * confirmed it.
 *
 * Unlike {@link broadcastPlanLaneRefresh} this carries the change itself, which
 * is what lets it be sent optimistically. A refresh signal cannot be: a peer
 * told to re-read while the server still reports the old order reads the old
 * order and puts the reorder straight back. A peer told WHAT the new order is
 * applies it without reading anything, so there is no such race.
 *
 * The order is an intent, not a window — see `applyPlanEntityReorderTokens` for
 * why that distinction is what makes this safe to send to a tab paginated to a
 * different depth. If the server later disagrees, the origin tab follows up with
 * a refresh signal and every tab converges on truth.
 */ export const broadcastPlanLaneReorder = (projectId, phaseId, newOrder)=>{
    if (projectId == null || phaseId == null) {
        return;
    }
    getTabSync().post({
        t: 'custom',
        k: PLAN_LANE_REORDER,
        payload: {
            projectId,
            phaseId,
            newOrder: [
                ...newOrder
            ]
        }
    });
};
const isLaneMovePayload = (v)=>{
    const p = v;
    return typeof v === 'object' && v !== null && typeof p.projectId === 'number' && typeof p.destPhaseId === 'number' && typeof p.payload === 'object' && p.payload !== null && typeof p.payload.entityId === 'string' && typeof p.payload.fromPhaseId === 'number';
};
/**
 * Tell other tabs an entity moved to a different phase, before the server has
 * confirmed it.
 *
 * Replaces a pair of refresh signals on the happy path. Those could only be sent
 * after the move persisted (a peer that re-reads too early sees the old
 * membership), and cost every peer showing either lane a fetch — so the card
 * arrived a round-trip plus a fetch after the drop. This carries the move
 * itself, so the peer applies it with no request at all.
 *
 * Safe to send early for the same reason as the reorder signal: the payload is
 * anchors, not indices or windows, so each tab resolves it against its own
 * lanes. If the server disagrees, or rejects, the origin tab follows up with the
 * refresh signals and everyone converges on truth.
 */ export const broadcastPlanLaneMove = (projectId, destPhaseId, payload)=>{
    if (projectId == null || destPhaseId == null) {
        return;
    }
    getTabSync().post({
        t: 'custom',
        k: PLAN_LANE_MOVE,
        payload: {
            projectId,
            destPhaseId,
            payload
        }
    });
};
/**
 * Apply peer lane-refresh signals. `refreshPlanLanesForPhase` no-ops when this
 * tab has no lane loaded for that phase, and routes through the shared fetcher,
 * so a burst of signals collapses into one request per lane.
 */ export const attachPlanBoardTabSync = ()=>getTabSync().subscribe((msgs)=>{
        msgs.forEach((msg)=>{
            if (msg.t !== 'custom') {
                return;
            }
            // Reorder: apply the peer's new order to OUR window. Synchronous and
            // read-free, so the card moves in this tab within a tick of the drop in
            // the other one. `applyPlanEntityReorderTokens` (not the broadcasting
            // wrapper) — re-broadcasting here would ping-pong forever.
            if (msg.k === PLAN_LANE_REORDER) {
                if (!isLaneReorderPayload(msg.payload)) {
                    return;
                }
                const { projectId, phaseId, newOrder } = msg.payload;
                applyPlanEntityReorderTokens(projectId, phaseId, newOrder);
                return;
            }
            // Cross-phase move: re-file the token here from the anchors the origin
            // tab sent, rather than fetching both lanes to discover the same thing.
            // `applyPlanEntityMoveTokens` writes only `source: 'local'`, so nothing
            // goes back on the wire and no loop is possible.
            if (msg.k === PLAN_LANE_MOVE) {
                if (!isLaneMovePayload(msg.payload)) {
                    return;
                }
                const { projectId, destPhaseId, payload } = msg.payload;
                const { applied } = applyPlanEntityMoveTokens(projectId, destPhaseId, payload, {
                    requireEntity: true
                });
                if (!applied) {
                    // This tab holds the destination lane but not the moved task, so it
                    // has no entity to render the card from. Read the lane instead of
                    // inserting a token with nothing behind it.
                    refreshPlanLanesForPhase(projectId, destPhaseId).catch((error)=>logger.error('plan-board tab-sync move fallback failed', {
                            error,
                            projectId,
                            destPhaseId
                        }));
                }
                return;
            }
            if (msg.k !== PLAN_LANE_REFRESH) {
                return;
            }
            if (!isLaneRefreshPayload(msg.payload)) {
                return;
            }
            refreshPlanLanesForPhase(msg.payload.projectId, msg.payload.phaseId).catch((error)=>logger.error('plan-board tab-sync lane refresh failed', {
                    error,
                    ...msg.payload
                }));
        });
    });
